import React, { useState } from 'react';
import moment from 'moment';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import * as Yup from 'yup';
import { PauseTypography } from '../../../components/PauseTypography';
import { PauseTable } from '../../../components/PauseTable';
import {
  PauseTableBody,
  PauseTableBodyCell,
  PauseTableRow,
} from '../../../components/PauseTable/components/PauseTableRow';
import { PauseForm } from '../../../components/PauseForm';
import { PauseFormAutocomplete } from '../../../components/PauseFormField2/PauseFormAutocomplete';
import { triggerDownload } from '../../../../utils/downloader';
import { PauseButton } from '../../../components/PauseButton';
import { PauseSubmitButton } from '../../../components/PauseSubmitButton';
import { HowtoLink } from '../../../components/HowtoLink';
import { PauseRadio } from '../../../components/PauseRadio';

const currentMonth = moment().format('MMMM');
const CurrentCycleText = () => {
  const formik = useFormikContext();
  const isCurrentCycle = get(formik, 'values.selection.isCurrentCycle');
  return isCurrentCycle ? (
    <PauseTypography
      css={(theme) => ({
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(1.5),
      })}
      variant="body2"
    >
      Report includes all leaves applied till end of
      {' '}
      {currentMonth}
      .
    </PauseTypography>
  ) : null;
};

interface ReportsProps extends React.PropsWithChildren<{}> {
  options: any[],
  onDownloadReport: (a: { startDate: string, endDate: string }) => any,
  onDownloadLeavesRecord: (a: {
    startDate: string,
    endDate: string,
    includeArchived?: boolean,
  }) => Promise<any>,
  orgName: string,
}
export const Reports = ({
  options = [],
  onDownloadReport = () => null,
  onDownloadLeavesRecord = () => Promise.resolve(null),
  orgName = '',
}: ReportsProps) => {
  enum ExportType {
    LeavesRecord,
    BalanceSummary,
  }
  const [exportType, setExportType] = useState<ExportType>(ExportType.BalanceSummary);
  return (
    <div css={{
      maxWidth: '75%',
    }}
    >
      <div css={(theme) => ({
        marginBottom: theme.spacing(4),
      })}
      >
        <PauseTypography variant="header">
          Download leave balance
        </PauseTypography>
      </div>
      {/* @ts-ignore */}
      <PauseForm
        enableReinitialize
        initialValues={{
          selection: get(options, '0', null),
        }}
        validationSchema={Yup.object().shape({
          selection: Yup.mixed()
            .test('nonnull', '', (v) => v !== null)
            .test('valid-range', '', (v) => v !== null && moment(v.startDate).isBefore(v.endDate))
            .required(),
        })}
        onSubmit={async (values) => {
          if (!values?.selection) return Promise.reject();
          const { startDate, endDate } = values.selection;
          if (startDate && endDate) {
            const res = exportType === ExportType.BalanceSummary ? await onDownloadReport({ startDate, endDate })
              : await onDownloadLeavesRecord({ startDate, endDate });
            const format = 'csv';
            const fileName = `${orgName}_${startDate}_${endDate}.${format}`;
            if (res) {
              triggerDownload({
                fileName,
                fileContents: res,
              });
            }
            return Promise.resolve();
          }
          return Promise.reject();
        }}
      >
        <div css={(theme) => ({
          // display: 'grid',
          // gridGap: theme.spacing(2),
          // gridAutoFlow: 'rows',
          // gridTemplateRows: 'auto',
          background: 'white',
          // padding: theme.spacing(3),
          borderRadius: theme.spacing(1),
          boxShadow: theme.shadows[0],
        })}
        >

          <PauseTable>
            <PauseTableBody>
              <PauseTableRow>
                <PauseTableBodyCell>
                  <div css={(theme) => ({
                    display: 'grid',
                    gridAutoFlow: 'rows',
                    gridTemplateRows: 'auto',
                    gridGap: theme.spacing(3),
                  })}
                  >
                    <div css={(theme) => ({
                      display: 'grid',
                      gridAutoFlow: 'rows',
                      gridTemplateRows: 'auto',
                      gridGap: theme.spacing(1.5),
                    })}
                    >
                      <PauseTypography variant="subtitle" color="pause.primary.medium">
                        What duration do you want data for?
                      </PauseTypography>
                      <HowtoLink
                        linkText="How to download balance report?"
                        link="https://support.getpause.com/en/article/export-leave-related-data-for-your-organisation-1rl69bn/"
                      />
                    </div>
                    <div>
                      <PauseFormAutocomplete
                        name="selection"
                              /* @ts-ignore */
                        fieldProps={{
                          options,
                          label: 'Leave cycle',
                        }}
                      />
                      <CurrentCycleText />
                    </div>
                  </div>
                </PauseTableBodyCell>
              </PauseTableRow>
              <PauseTableRow>
                <PauseTableBodyCell>
                  <div css={(t) => ({
                    display: 'grid',
                    gridGap: t.spacing(3),
                    gridAutoFlow: 'rows',
                  })}
                  >
                    <PauseTypography variant="subtitle" color="pause.primary.medium">
                      What kind of a report do you want?
                    </PauseTypography>
                    <PauseRadio
                      checked={exportType === ExportType.BalanceSummary}
                      label="Summary of leave balances"
                      onChange={() => setExportType(ExportType.BalanceSummary)}
                    />
                    <PauseRadio
                      checked={exportType === ExportType.LeavesRecord}
                      label="Record of all leaves"
                      onChange={() => setExportType(ExportType.LeavesRecord)}
                    />
                  </div>
                </PauseTableBodyCell>
              </PauseTableRow>
            </PauseTableBody>
          </PauseTable>
        </div>
        <div css={(theme) => ({
          marginTop: theme.spacing(2),
        })}
        >
          <PauseSubmitButton
            disableIfFormNotValid
            enableForInitialValid
          >
            <PauseButton>
              Download
              {' '}
              {
              exportType === ExportType.LeavesRecord ? 'leaves record' : 'balance summary'
            }
            </PauseButton>
          </PauseSubmitButton>
        </div>
      </PauseForm>

    </div>
  );
};
